"use client";

import { cn } from "@/lib/utils";
import { TRANSPARENT_COLOR_NAME, checkerboardStyle, isTransparentHex } from "@/lib/config/colors";

interface ColorSwatchProps {
  /** The hex color code for solid colors */
  hex?: string;
  /** Whether this represents a multicolor product */
  isMulticolor?: boolean;
  /** List of hex colors for multicolor products */
  multicolorList?: string[];
  /** Optional size in pixels or tailwind class */
  size?: "sm" | "md" | "lg" | "xl" | number;
  /** Additional classes */
  className?: string;
  /** Optional border color */
  borderColor?: string;
}

/**
 * A reusable component to render a color swatch.
 * Supports solid colors and multicolor gradients. The reserved Transparent
 * hex (lib/config/colors.ts) draws as a checkerboard.
 */
export function ColorSwatch({
  hex = "#808080",
  isMulticolor = false,
  multicolorList = [],
  size = "md",
  className,
  borderColor,
}: ColorSwatchProps) {
  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-6 w-6",
    lg: "h-8 w-8",
    xl: "h-10 w-10",
  };

  const style: React.CSSProperties = {};

  // A single clear colour (solid, or a one-colour "multicolor" set).
  const isTransparent = isMulticolor && multicolorList.length > 0
    ? multicolorList.length === 1 && isTransparentHex(multicolorList[0])
    : isTransparentHex(hex);

  if (isTransparent) {
    Object.assign(style, checkerboardStyle());
  } else if (isMulticolor && multicolorList.length > 0) {
    if (multicolorList.length === 1) {
      style.backgroundColor = multicolorList[0];
    } else {
      // Create a sharp conic gradient for multicolor
      const segments = multicolorList.map((color, index) => {
        const start = (index * 100) / multicolorList.length;
        const end = ((index + 1) * 100) / multicolorList.length;
        return `${color} ${start}% ${end}%`;
      });
      style.background = `conic-gradient(${segments.join(", ")})`;
    }
  } else {
    style.backgroundColor = hex;
  }

  if (borderColor) {
    style.borderColor = borderColor;
  }

  // Common styles for both modes
  const isWhite = hex.toLowerCase() === "#ffffff" || (isMulticolor && multicolorList.some(c => c.toLowerCase() === "#ffffff"));
  // Near-white squares need the same outline as white to show on a light card.
  const needsOutline = isWhite || isTransparent || (isMulticolor && multicolorList.some(isTransparentHex));

  return (
    <div
      className={cn(
        "rounded-full border shadow-sm",
        typeof size === "string" ? sizeClasses[size] : "",
        needsOutline ? "border-gray-300" : "border-transparent",
        className
      )}
      style={{
        ...style,
        width: typeof size === "number" ? size : undefined,
        height: typeof size === "number" ? size : undefined,
      }}
      title={isTransparent ? TRANSPARENT_COLOR_NAME : isMulticolor ? `Multicolor: ${multicolorList.join(", ")}` : hex}
    />
  );
}
